import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Cloudflare Turnstile (widget "Meu Treino login e cadastro"). The site key is public; the API holds the secret.
// Local hosts get Cloudflare's always-pass test key, since the real one only answers on the two domains.
const SITE_KEY = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) ? '1x00000000000000000000AA' : '0x4AAAAAAFnfxpJs17dantzP';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 15000;

let scriptPromise;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      scriptPromise = undefined;
      reject(new Error('Turnstile failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

// Invisible unless Cloudflare wants an interaction. The form asks for a token on submit:
// getToken() waits for the current check, reset() gets a fresh one (tokens are single use).
// theme: 'light' or 'dark' to match the window it sits in ('auto' follows the device).
export const TurnstileField = forwardRef(({ action, theme = 'auto' }, ref) => {
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const tokenRef = useRef('');
  const waitersRef = useRef([]);
  const [isInteractive, setIsInteractive] = useState(false);
  const isInteractiveRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const settle = (token) => {
      tokenRef.current = token;
      waitersRef.current.splice(0).forEach((resolve) => resolve(token));
    };

    loadTurnstile()
      .then((turnstile) => {
        if (!isMounted || !containerRef.current) return;
        widgetRef.current = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          appearance: 'interaction-only',
          theme,
          language: i18n.language === 'pt' ? 'pt-br' : 'en',
          callback: settle,
          'before-interactive-callback': () => {
            isInteractiveRef.current = true;
            setIsInteractive(true);
          },
          'after-interactive-callback': () => {
            isInteractiveRef.current = false;
            setIsInteractive(false);
          },
          'expired-callback': () => {
            tokenRef.current = '';
          },
          'error-callback': () => {
            settle('');
            // Returning true tells Turnstile the error is handled; it retries on its own
            return true;
          },
        });
      })
      .catch(() => settle(''));

    return () => {
      isMounted = false;
      if (widgetRef.current !== null) window.turnstile?.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [action, theme, i18n.language]);

  useImperativeHandle(ref, () => ({
    getToken: () =>
      tokenRef.current
        ? Promise.resolve(tokenRef.current)
        : new Promise((resolve) => {
            waitersRef.current.push(resolve);
            // Give up on a check that never answers, but not while Cloudflare is waiting on the person's click
            const giveUp = () => (isInteractiveRef.current ? setTimeout(giveUp, TOKEN_TIMEOUT_MS) : resolve(tokenRef.current));
            setTimeout(giveUp, TOKEN_TIMEOUT_MS);
          }),
    reset: () => {
      tokenRef.current = '';
      if (widgetRef.current !== null) window.turnstile?.reset(widgetRef.current);
    },
  }));

  return <div ref={containerRef} className={`mt-auth__turnstile${isInteractive ? ' mt-auth__turnstile--shown' : ''}`} />;
});

TurnstileField.displayName = 'TurnstileField';
